import React, { useState, useEffect } from 'react';
import {
  View,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  RefreshControl,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import AppShell from '../components/layout/AppShell';
import ThemedText from '../components/common/ThemedText';
import { useTheme } from '../theme/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import { apiBaseUrl } from '../config/env';
import { storage } from '../utils/storage';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  referenceId: string | null;
  referenceType: string | null;
  metadata: any;
  read: boolean;
  createdAt: string;
}

const NotificationScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation();
  const { clearBadge } = useNotification();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Mark all notifications as read and clear the TopBar badge when screen is focused
  useFocusEffect(
    React.useCallback(() => {
      clearBadge();
      markAsRead();
    }, [])
  );

  const fetchNotifications = async () => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      const response = await fetch(`${apiBaseUrl}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  const markAsRead = async (notificationId?: string) => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      await fetch(`${apiBaseUrl}/api/notifications/mark-read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          notificationIds: notificationId ? [notificationId] : undefined,
        }),
      });

      // Update local state
      if (notificationId) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } else {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const token = await storage.getToken();
      if (!token) return;

      const response = await fetch(
        `${apiBaseUrl}/api/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationPress = async (notification: Notification) => {
    console.log('🔔 [NOTIFICATION PRESS] Notification tapped:', {
      id: notification.id,
      type: notification.type,
      referenceType: notification.referenceType,
      referenceId: notification.referenceId,
      title: notification.title,
    });

    // Mark as read
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate based on type
    if (notification.referenceType === 'club' && notification.referenceId) {
      // Check if this is a meetup notification
      const isMeetupNotification = 
        notification.type === 'MEETUP_REQUEST' || 
        notification.type === 'MEETUP_ACCEPTED' || 
        notification.type === 'MEETUP_REJECTED';

      // Check if this is a referral intro notification
      // POST_COMMENTED is used for referral intros (both old and new format)
      const isReferralNotification = 
        (notification.metadata?.isReferralIntro && notification.metadata?.clubId) ||
        (notification.type === 'POST_COMMENTED' && notification.title?.includes('Referral')) ||
        (notification.type === 'QUOTATION_ACCEPTED' && notification.title?.includes('Intro')) ||
        (notification.type === 'QUOTATION_REJECTED' && notification.title?.includes('Intro'));
      
      if (isMeetupNotification) {
        console.log('📍 [NOTIFICATION PRESS] Navigating to club meetups tab:', notification.referenceId);
        (navigation as any).navigate('MainTabs', {
          screen: 'Clubs',
          params: {
            screen: 'ClubDetail',
            params: { 
              clubId: notification.referenceId,
              initialTab: 'meetups',
            },
          },
        });
      } else if (isReferralNotification) {
        const clubId = notification.metadata?.clubId;
        console.log('📍 [NOTIFICATION PRESS] Navigating to referral request:', {
          requestId: notification.referenceId,
          clubId: clubId || 'unknown',
          hasClubId: !!clubId,
        });
        
        if (!clubId) {
          // Old notification without clubId - just show the notification was tapped
          Alert.alert(
            'View Referral',
            'Please open the Clubs tab and check your referral requests manually.',
            [{ text: 'OK' }]
          );
          return;
        }
        
        (navigation as any).navigate('MainTabs', {
          screen: 'Clubs',
          params: {
            screen: 'ReferralRequestDetail',
            params: { 
              requestId: notification.referenceId,
              clubId: clubId,
            },
          },
        });
      } else {
        console.log('📍 [NOTIFICATION PRESS] Navigating to club:', notification.referenceId);
        (navigation as any).navigate('MainTabs', {
          screen: 'Clubs',
          params: {
            screen: 'ClubDetail',
            params: { clubId: notification.referenceId },
          },
        });
      }
    } else if (
      notification.referenceType === 'ledger' &&
      notification.referenceId
    ) {
      console.log('📍 [NOTIFICATION PRESS] Navigating to ledger');
      // For ledger notifications, just navigate to ledger section
      (navigation as any).navigate('MainTabs', {
        screen: 'Ledger',
      });
    } else if (
      notification.referenceType === 'connection' &&
      notification.referenceId
    ) {
      console.log('📍 [NOTIFICATION PRESS] Navigating to connection');
      // Handle connection notifications based on type
      if (notification.type === 'CONNECTION_REQUEST_RECEIVED') {
        // Navigate to Network tab with requests view
        (navigation as any).navigate('MainTabs', {
          screen: 'Network',
          params: { initialView: 'requests' },
        });
      } else if (notification.type === 'CONNECTION_REQUEST_ACCEPTED') {
        // Navigate to Network tab with my-network view
        (navigation as any).navigate('MainTabs', {
          screen: 'Network',
          params: { initialView: 'my-network' },
        });
      } else {
        // Default to network tab
        (navigation as any).navigate('MainTabs', {
          screen: 'Network',
        });
      }
    } else if (
      notification.referenceType === 'quote' &&
      notification.referenceId
    ) {
      const jobId = notification.metadata?.jobId;
      if (jobId) {
        console.log('📍 [NOTIFICATION PRESS] Navigating to contract details for job:', jobId);
        // Navigate to contract (needs jobId)
        (navigation as any).navigate('MainTabs', {
          screen: 'Work',
          params: {
            screen: 'ContractDetail',
            params: { 
              jobId,
            },
          },
        });
      } else {
        console.log('⚠️ [NOTIFICATION PRESS] Missing jobId in metadata for quote notification');
        Alert.alert(
          'Unable to open contract',
          'This notification does not include the contract details. Please open the Work tab manually.'
        );
        (navigation as any).navigate('MainTabs', {
          screen: 'Work',
        });
      }
    } else {
      console.log('⚠️ [NOTIFICATION PRESS] No navigation rule matched');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'CLUB_JOIN_REQUEST':
        return { name: 'person-add-outline', color: '#F59E0B' };
      case 'CLUB_REQUEST_APPROVED':
        return { name: 'checkmark-circle', color: '#10B981' };
      case 'CLUB_REQUEST_DENIED':
        return { name: 'close-circle', color: '#EF4444' };
      case 'NEW_QUOTATION':
        return { name: 'document-text', color: '#3B82F6' };
      case 'LEDGER_SHARED':
        return { name: 'receipt', color: '#8B5CF6' };
      case 'LEDGER_ACCEPTED':
        return { name: 'checkmark-done', color: '#10B981' };
      case 'LEDGER_REJECTED':
        return { name: 'close', color: '#EF4444' };
      case 'CONNECTION_REQUEST_RECEIVED':
        return { name: 'person-add', color: '#0A66C2' };
      case 'CONNECTION_REQUEST_ACCEPTED':
        return { name: 'people', color: '#10B981' };
      case 'CONNECTION_REQUEST_REJECTED':
        return { name: 'person-remove', color: '#EF4444' };
      default:
        return { name: 'notifications', color: theme.accent };
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return `${days}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  const renderNotification = ({ item }: { item: Notification }) => {
    const icon = getNotificationIcon(item.type);

    return (
      <TouchableOpacity
        style={[
          styles.notificationCard,
          {
            backgroundColor: item.read ? theme.card : theme.backgroundSecondary,
            borderColor: theme.border,
          },
        ]}
        onPress={() => handleNotificationPress(item)}
        onLongPress={() => {
          Alert.alert('Delete Notification', 'Remove this notification?', [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Delete',
              style: 'destructive',
              onPress: () => deleteNotification(item.id),
            },
          ]);
        }}
        activeOpacity={0.7}
      >
        <View style={styles.iconContainer}>
          <Ionicons name={icon.name as any} size={28} color={icon.color} />
          {!item.read && <View style={[styles.unreadDot, { backgroundColor: theme.accent }]} />}
        </View>

        <View style={styles.contentContainer}>
          <ThemedText style={styles.title}>{item.title}</ThemedText>
          <ThemedText style={[styles.message, { color: theme.textSecondary }]}>
            {item.message}
          </ThemedText>
          <ThemedText style={[styles.timestamp, { color: theme.textTertiary }]}>
            {formatTimestamp(item.createdAt)}
          </ThemedText>
        </View>

        <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
      </TouchableOpacity>
    );
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="notifications-off-outline" size={64} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
        No Activies yet
      </ThemedText>
    </View>
  );

  return (
    <AppShell
      title="Activities"
      scrollable={false}
      showBackButton={true}
    >
      {unreadCount > 0 && (
        <View style={[styles.headerAction, { borderBottomColor: theme.border }]}>
          <TouchableOpacity
            onPress={() => markAsRead()}
            style={styles.markAllButton}
            activeOpacity={0.7}
          >
            <ThemedText style={[styles.markAllText, { color: theme.accent }]}>
              Mark all read
            </ThemedText>
          </TouchableOpacity>
        </View>
      )}
      <FlatList
        data={notifications}
        renderItem={renderNotification}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContainer}
        ListEmptyComponent={!loading ? renderEmpty : null}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={theme.accent}
          />
        }
      />
    </AppShell>
  );
};

const styles = StyleSheet.create({
  listContainer: {
    padding: 16,
  },
  notificationCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  iconContainer: {
    marginRight: 12,
    position: 'relative',
  },
  unreadDot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  contentContainer: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  message: {
    fontSize: 14,
    marginBottom: 4,
    lineHeight: 20,
  },
  timestamp: {
    fontSize: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
  },
  headerAction: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    alignItems: 'flex-end',
  },
  markAllButton: {
    padding: 8,
  },
  markAllText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default NotificationScreen;
